import {
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  ExpandableSection,
  FileInput,
  Input,
  Modal,
  ProgressBar,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  Toggle,
  Form,
  Alert,
  FormField,
  StatusIndicator,
} from '@cloudscape-design/components';
import { ErrorMessage } from './ErrorMessage';
import { uploadDripContactsExcel } from '../common/api/dripContacts';
import { Task, TaskCreateResponse } from '../common/types';
import { getTask } from '../common/api/tasks';
import {
  TASK_DETAILED_INFO_MAP,
  TASK_NAME_MAP,
  TASK_STATUS_INFO_MAP,
  TASK_STATUS_MAP,
} from './PlaceStatus';

export type TaskMonitorModalProps = {
  task_uuid?: string | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onComplete?: () => void;
};

export function TaskMonitorModal(props: TaskMonitorModalProps) {
  const { task_uuid, open, setOpen, onComplete } = props;

  const [task, setTask] = useState<Task | null>(null);

  const close = () => {
    setTask(null);
    setOpen(false);
    onComplete?.();
  };

  const getTaskMutation = useMutation({
    mutationFn: getTask,
    onSuccess: (data) => {
      if (data) setTask(data);
    },
  });

  useEffect(() => {
    if (task_uuid && open) {
      if (!task) {
        getTaskMutation.mutate(task_uuid);
      }
      const interval = setInterval(() => {
        if (task?.status === 'in-progress' || task?.status === 'pending') {
          getTaskMutation.mutate(task_uuid);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [task_uuid, open, task]);

  const loading = !task || task?.status === 'in-progress' || task?.status === 'pending';
  const currentName = task ? TASK_NAME_MAP[task.name] : 'Loading...';

  const status = useMemo(() => {
    if (!task) {
      return 'in-progress';
    }
    if (task.status === 'pending') {
      return 'in-progress';
    }
    if (task.status === 'in-progress') {
      return 'in-progress';
    }
    // check for specific task name
    if (task.status === 'completed') {
      return 'success';
    }
    if (task.status === 'failed' && task.retries && task.retries >= 3) {
      return 'error';
    }
    return 'in-progress';
  }, [task]);

  const currentInfo = task ? TASK_DETAILED_INFO_MAP[task.name] : 'Loading...';

  return (
    <Modal
      size="medium"
      onDismiss={close}
      visible={open}
      closeAriaLabel="Close"
      header={
        <Header
          variant="h2"
          description={
            <SpaceBetween direction="vertical" size="xxs">
              <StatusIndicator
                type={
                  TASK_STATUS_MAP[task?.status ?? 'pending'] === 'in-progress'
                    ? 'loading'
                    : TASK_STATUS_MAP[task?.status ?? 'pending']
                }
              >
                {TASK_STATUS_INFO_MAP[task?.status ?? 'pending']}
              </StatusIndicator>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="xxs">
            {task ? TASK_NAME_MAP[task.name] : 'Loading...'}
          </SpaceBetween>
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {task && task.progress !== undefined ? (
          <ProgressBar
            label={currentName}
            // in-progress, success, or error
            status={status}
            // 0 to 100 as a percentage
            value={status === 'in-progress' ? (task.progress ?? undefined) : 100}
            additionalInfo={currentInfo}
          />
        ) : (
          <Box variant="span">{currentInfo}</Box>
        )}
        {loading && (!task || task.progress === undefined) && <Spinner />}
        {getTaskMutation.error && !loading && (
          <ErrorMessage loading={getTaskMutation.isPending} error={getTaskMutation.error} />
        )}
      </SpaceBetween>
    </Modal>
  );
}
